import { ACA_CAMPAIGN_CONFIG } from '../../campaigns/aca-health-insurance/config/aca.config'

export interface CampaignStep {
  id: string
  component: string
  order: number
  label: string
  description?: string
  isOptional?: boolean
  autoCompleteIfEmpty?: boolean
}

export interface CampaignConfig {
  name: string
  description: string
  icon: string
  steps: CampaignStep[]
  fieldMapping: Record<string, string[]>
  optionalSteps?: string[]
}

// In-memory cache for campaign configurations
let CAMPAIGNS_CACHE: Record<string, CampaignConfig> = {}

/**
 * Load campaign configurations from database
 * This should be called when the app initializes
 */
export const loadCampaignsFromDatabase = async (campaigns: any[]): Promise<void> => {
  CAMPAIGNS_CACHE = {}

  campaigns.forEach(campaign => {
    if (campaign.stepConfiguration) {
      const config = campaign.stepConfiguration as any

      // ✅ Use campaign.id as PRIMARY KEY (guaranteed unique)
      CAMPAIGNS_CACHE[campaign.id] = {
        name: campaign.name,
        description: config.description || '',
        icon: config.icon || 'FileText',
        steps: config.steps || [],
        fieldMapping: config.fieldMapping || {},
        optionalSteps: config.optionalSteps || []
      }

      // Note: We do NOT store by type since multiple campaigns can have same type
    }
  })

}

/**
 * Get campaign configuration by ID
 */
export const getCampaignConfig = (campaignId: string | null | undefined): CampaignConfig | null => {
  if (!campaignId) {
    console.warn('⚠️ No campaign ID provided to getCampaignConfig')
    return null
  }

  const config = CAMPAIGNS_CACHE[campaignId]

  // ✅ CRITICAL FIX: Always use hardcoded ACA config (6 steps) for ACA campaigns
  const name = config?.name?.toLowerCase() || '';
  const nameMatches = name.includes('aca') || name.includes('health insurance');
  const idMatches = campaignId === 'campaign-4' || campaignId === 'campaign-5' || campaignId === 'aca-health-insurance';

  if (idMatches || nameMatches) {
    console.log('🚀 [campaigns.config] Using hardcoded ACA config (6 steps) for:', campaignId || config?.name);
    return ACA_CAMPAIGN_CONFIG as unknown as CampaignConfig
  }

  if (!config) {
    // Other fallbacks...
    if (campaignId === ACA_CAMPAIGN_CONFIG.campaignType) {
      console.log('🔄 [campaigns.config] Falling back to hardcoded ACA config for:', campaignId);
      return ACA_CAMPAIGN_CONFIG as unknown as CampaignConfig
    }

    console.error('❌ Campaign not found in cache:', campaignId)
    return null
  }

  console.log('📦 [campaigns.config] Found config in DB cache for:', campaignId, 'Steps:', config.steps?.length);
  return config
}

/**
 * Get hardcoded configuration by name or identifier
 * Useful when database ID does not match but name does
 */
export const getHardcodedConfig = (identifier: string | null | undefined): CampaignConfig | null => {
  if (!identifier) return null

  // Check ACA Campaign
  if (ACA_CAMPAIGN_CONFIG.campaignNames.includes(identifier) ||
    ACA_CAMPAIGN_CONFIG.name === identifier ||
    ACA_CAMPAIGN_CONFIG.campaignType === identifier) {
    return ACA_CAMPAIGN_CONFIG as unknown as CampaignConfig
  }

  return null
}

/**
 * Get all campaign steps
 */
export const getCampaignSteps = (campaignId: string | null | undefined): CampaignStep[] => {
  const campaign = getCampaignConfig(campaignId)
  return campaign?.steps || []
}

/**
 * Get specific step configuration
 */
export const getStepConfig = (campaignId: string, stepIndex: number): CampaignStep | null => {
  const steps = getCampaignSteps(campaignId)
  return steps[stepIndex] || null
}

/**
 * Get total number of steps
 */
export const getTotalSteps = (campaignId: string | null | undefined): number => {
  return getCampaignSteps(campaignId).length
}

/**
 * Get fields for a specific step
 */
export const getStepFields = (campaignId: string, stepId: string): string[] => {
  const campaign = getCampaignConfig(campaignId)
  return campaign?.fieldMapping[stepId] || []
}

/**
 * Get all campaigns
 */
export const getAllCampaigns = (): CampaignConfig[] => {
  // Return unique campaigns (avoid duplicates from multiple cache keys)
  const uniqueCampaigns = new Map<string, CampaignConfig>()

  Object.values(CAMPAIGNS_CACHE).forEach(config => {
    if (!uniqueCampaigns.has(config.name)) {
      uniqueCampaigns.set(config.name, config)
    }
  })

  return Array.from(uniqueCampaigns.values())
}

/**
 * Check if a step is optional
 */
export const isStepOptional = (campaignId: string, stepId: string): boolean => {
  const campaign = getCampaignConfig(campaignId)
  return campaign?.optionalSteps?.includes(stepId) || false
}

/**
 * Check if step should auto-complete when empty
 */
export const shouldAutoCompleteEmptyStep = (campaignId: string, stepIndex: number): boolean => {
  const campaign = getCampaignConfig(campaignId)
  const step = campaign?.steps[stepIndex]
  return step?.autoCompleteIfEmpty || false
}

/**
 * Get campaign by type enum (if needed for filtering)
 */
export const getCampaignsByType = (type: 'TYPE_1' | 'TYPE_2' | 'TYPE_3'): CampaignConfig[] => {
  // Note: This is inefficient since we don't store by type anymore
  // If you need this often, consider adding a separate type index
  return getAllCampaigns().filter(config => {
    // We'd need to pass campaign type info differently
    // For now, this won't work without additional data
    console.warn('getCampaignsByType not supported in current implementation')
    return false
  })
}

// Export for debugging
export const getCampaignCache = () => CAMPAIGNS_CACHE